import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Popover,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddCircleOutline as AddDateIcon,
  CalendarMonth as CalendarIcon,
  Close as CloseIcon,
  EventRepeat as NextMonthIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import {
  countPrescriptionsToCreate,
  formatScheduledDatePtBr,
  isIsoYmdDate,
  isPrescriptionScheduledSpecialItem,
  normalizeScheduledIssueDates,
} from '../../utils/prescriptionBatchCreate';
import { addMonthsToIsoDate } from '../../utils/prescriptionMultiMonth';
import { todayYmdBr } from '../../utils/localCalendarDate';

export interface PrescriptionEmitOptionsBarProps {
  format: 'structured' | 'free_text';
  medicationCount: number;
  splitMedications: boolean;
  onSplitMedicationsChange: (value: boolean) => void;
  scheduledDates: string[];
  onScheduledDatesChange: (dates: string[]) => void;
  /** Itens atuais — usados para programar só controlados/antimicrobianos. */
  items?: Array<{ medication_name?: string; is_controlled?: boolean; is_antimicrobial?: boolean }>;
  scheduleSpecialItemsOnly?: boolean;
  onScheduleSpecialItemsOnlyChange?: (value: boolean) => void;
  disabled?: boolean;
  hideIssueDateOnPrint?: boolean;
  onHideIssueDateOnPrintChange?: (value: boolean) => void;
  /** Na edição de receita existente: só opções de impressão (sem emissão em lote). */
  printOptionsOnly?: boolean;
}

const PrescriptionEmitOptionsBar: React.FC<PrescriptionEmitOptionsBarProps> = ({
  format,
  medicationCount,
  splitMedications,
  onSplitMedicationsChange,
  scheduledDates,
  onScheduledDatesChange,
  items = [],
  scheduleSpecialItemsOnly = false,
  onScheduleSpecialItemsOnlyChange,
  disabled = false,
  hideIssueDateOnPrint = false,
  onHideIssueDateOnPrintChange,
  printOptionsOnly = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [pendingDate, setPendingDate] = useState(todayYmdBr());
  const [sequenceMonths, setSequenceMonths] = useState(6);

  const dates = useMemo(
    () => normalizeScheduledIssueDates(scheduledDates),
    [scheduledDates],
  );

  const namedItems = useMemo(
    () => items.filter((i) => String(i.medication_name || '').trim()),
    [items],
  );
  const specialMedicationCount = useMemo(
    () => namedItems.filter((i) => isPrescriptionScheduledSpecialItem(i)).length,
    [namedItems],
  );
  const regularMedicationCount = namedItems.length - specialMedicationCount;
  const canScheduleSpecialOnly =
    format === 'structured'
    && regularMedicationCount > 0
    && specialMedicationCount > 0
    && dates.length > 1;

  const prescriptionCount = countPrescriptionsToCreate({
    format,
    medicationCount,
    splitMedications,
    scheduledDates: dates,
    items: namedItems as Record<string, unknown>[],
    scheduleSpecialItemsOnly: scheduleSpecialItemsOnly && canScheduleSpecialOnly,
  });

  const open = Boolean(anchorEl);
  const splitDisabled = disabled || format !== 'structured' || medicationCount < 2;

  const addDate = () => {
    if (!isIsoYmdDate(pendingDate)) return;
    onScheduledDatesChange(normalizeScheduledIssueDates([...dates, pendingDate]));
    setPendingDate(todayYmdBr());
  };

  const addNextMonth = () => {
    // Conta a partir da 1ª data para não "encolher" o dia após um fevereiro (31 → 28 → 28…).
    const first = dates[0] || todayYmdBr();
    const last = dates[dates.length - 1] || first;
    let next = '';
    for (let k = 1; k <= 36; k += 1) {
      const candidate = addMonthsToIsoDate(first, k);
      if (candidate > last) { next = candidate; break; }
    }
    if (!next) return;
    onScheduledDatesChange(normalizeScheduledIssueDates([...dates, next]));
  };

  const generateMonthSequence = () => {
    const n = Math.min(12, Math.max(1, Math.floor(Number(sequenceMonths) || 1)));
    const start = dates[0] || todayYmdBr();
    const built: string[] = [];
    for (let i = 0; i < n; i += 1) {
      const iso = addMonthsToIsoDate(start, i);
      if (iso) built.push(iso);
    }
    onScheduledDatesChange(normalizeScheduledIssueDates(built));
  };

  const removeDate = (iso: string) => {
    if (dates.length <= 1) return;
    onScheduledDatesChange(dates.filter((d) => d !== iso));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
        py: 1.5,
        px: 2,
        borderRadius: 2,
        border: '1px solid #e5e7eb',
        bgcolor: '#f8fafc',
      }}
    >
      {!printOptionsOnly && (
      <FormControlLabel
        disabled={splitDisabled}
        control={
          <Switch
            size="small"
            checked={splitMedications && !splitDisabled}
            onChange={(e) => onSplitMedicationsChange(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Typography variant="body2" sx={{ fontWeight: 500, color: splitDisabled ? '#94a3b8' : '#334155' }}>
            Separar medicamentos em prescrições diferentes
          </Typography>
        }
      />
      )}

      {!printOptionsOnly && onScheduleSpecialItemsOnlyChange && (
        <FormControlLabel
          disabled={disabled || !canScheduleSpecialOnly}
          control={
            <Switch
              size="small"
              checked={scheduleSpecialItemsOnly && canScheduleSpecialOnly}
              onChange={(e) => onScheduleSpecialItemsOnlyChange(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: !canScheduleSpecialOnly ? '#94a3b8' : '#334155' }}
            >
              Programar datas só para controlados e antimicrobianos
            </Typography>
          }
        />
      )}

      {onHideIssueDateOnPrintChange && (
        <FormControlLabel
          disabled={disabled}
          control={
            <Switch
              size="small"
              checked={hideIssueDateOnPrint}
              onChange={(e) => onHideIssueDateOnPrintChange(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
              Ocultar data na impressão
            </Typography>
          }
        />
      )}

      {!printOptionsOnly && (
      <Button
        variant="contained"
        size="small"
        disabled={disabled}
        startIcon={<NextMonthIcon />}
        onClick={addNextMonth}
        sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
      >
        Adicionar próximo mês
      </Button>
      )}

      {!printOptionsOnly && (
      <Button
        variant="outlined"
        size="small"
        disabled={disabled}
        startIcon={<ScheduleIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          borderColor: '#3b82f6',
          color: '#2563eb',
          borderRadius: 999,
          px: 2,
        }}
      >
        Programar data
        {dates.length > 1 ? ` (${dates.length})` : ''}
      </Button>
      )}

      {!printOptionsOnly && prescriptionCount > 1 && (
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
          Serão geradas {prescriptionCount} receitas
          {scheduleSpecialItemsOnly && canScheduleSpecialOnly
            ? ` (${regularMedicationCount} comum${regularMedicationCount === 1 ? '' : 's'} na 1ª data; ${specialMedicationCount} especial${specialMedicationCount === 1 ? '' : 'is'} nas programadas)`
            : ''}
        </Typography>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: 2, p: 2, minWidth: 280, maxWidth: 340 } } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', letterSpacing: 0.4 }}>
            RECEITAS QUE SERÃO GERADAS:
          </Typography>
          <IconButton size="small" onClick={() => setAnchorEl(null)} sx={{ color: '#2563eb', mt: -0.5, mr: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          {dates.map((iso) => (
            <Box key={iso} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {formatScheduledDatePtBr(iso)}
                </Typography>
              </Box>
              {dates.length > 1 && (
                <IconButton size="small" onClick={() => removeDate(iso)} aria-label="Remover data">
                  <CloseIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <>
            <Button
              size="small"
              startIcon={<NextMonthIcon />}
              onClick={() => {
                addNextMonth();
              }}
              sx={{ textTransform: 'none', fontWeight: 600, color: '#2563eb', justifyContent: 'flex-start', pl: 0.5 }}
            >
              Adicionar próximo mês
            </Button>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                type="number"
                label="Meses em sequência"
                value={sequenceMonths}
                onChange={(e) => setSequenceMonths(Number(e.target.value))}
                inputProps={{ min: 1, max: 12, step: 1 }}
                sx={{ flex: 1 }}
              />
              <Button size="small" variant="outlined" onClick={generateMonthSequence} sx={{ textTransform: 'none', fontWeight: 600 }}>
                Gerar
              </Button>
            </Box>
          </>
          <TextField
            type="date"
            size="small"
            label="Nova data"
            value={pendingDate}
            onChange={(e) => setPendingDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <Button
            size="small"
            startIcon={<AddDateIcon />}
            onClick={addDate}
            disabled={!isIsoYmdDate(pendingDate) || dates.includes(pendingDate)}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#2563eb', justifyContent: 'flex-start', pl: 0.5 }}
          >
            Adicionar data
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default PrescriptionEmitOptionsBar;
